import {
  Add as AddIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Remove as RemoveIcon,
  Archive as SaveForLaterIcon,
  ShoppingCart as ShoppingCartIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  IconButton,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { CartItemProps, getPriceDisplay } from './types';
import { useCatalogInstance } from '../../../hooks/useCatalogInstance';

const CartItem: React.FC<CartItemProps> = ({
  item,
  onUpdateQuantity,
  onRemove,
  onSaveForLater,
  onMoveToCart,
  onToggleSelection,
  onViewDetails,
  onEditVariants,
}) => {
  const { getCatalogDisplayName } = useCatalogInstance();
  const [catalogDisplayName, setCatalogDisplayName] = useState<string>('Loading...');

  const additionalQuantity = Number(
    item.pricing_info?.additional_users ?? item.service_configuration?.additional_users ?? 0,
  );
  const multiLocationQuote = item.service_configuration?.multi_location_quote;
  const multiLocationSites = Array.isArray(multiLocationQuote?.sites) ? multiLocationQuote.sites : [];
  const quantityUnitLabel = String(item.pricing_info?.quantity_unit_label || 'user').trim() || 'user';
  const quantityUnitLabelPlural = additionalQuantity === 1 ? quantityUnitLabel : `${quantityUnitLabel}s`;

  useEffect(() => {
    const loadCatalogName = async () => {
      if (item.added_catalog_instance_id) {
        const name = await getCatalogDisplayName(item.added_catalog_instance_id);
        setCatalogDisplayName(name);
      } else {
        setCatalogDisplayName('Unknown Catalog');
      }
    };
    loadCatalogName();
  }, [item.added_catalog_instance_id, getCatalogDisplayName]);

  return (
    <Card
      sx={{
        border: `2px solid var(--catalog-primary-color, #1976d2)`,
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Selection Checkbox */}
        <Box sx={{ position: 'absolute', top: 16, left: 16 }}>
          <Checkbox
            checked={item.is_selected !== false}
            onChange={(e) => onToggleSelection(item.id, e.target.checked)}
            size="small"
            sx={{
              color: 'var(--catalog-primary-color, #1976d2)',
              '&.Mui-checked': {
                color: 'var(--catalog-primary-color, #1976d2)',
              },
            }}
          />
        </Box>

        {/* Service Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
            ml: 5,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              {item.service_name || `Service ${item.service_id}`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {item.provider_name || 'Service Provider'}
            </Typography>

            {/* Catalog Instance Badge */}
            {item.added_catalog_instance_id && (
              <Chip
                label={catalogDisplayName}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.7rem',
                  height: '20px',
                  borderColor: 'var(--catalog-primary-color, #1976d2)',
                  color: 'var(--catalog-primary-color, #1976d2)',
                }}
              />
            )}
          </Box>

          {/* Quantity Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
              disabled={item.quantity <= 1}
              sx={{ color: 'text.secondary' }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="body1"
              sx={{ minWidth: 30, textAlign: 'center', fontWeight: 'bold' }}
            >
              {item.quantity}
            </Typography>
            <IconButton
              size="small"
              onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
              sx={{ color: 'text.secondary' }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Selected Configuration */}
        <Box sx={{ mb: 2 }}>
          {/* Address */}
          {item.selected_address_label && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Service Address: {item.selected_address_label}
              </Typography>
            </Box>
          )}

          {/* Variants */}
          {item.variant_selections && Object.keys(item.variant_selections).length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Selected Options:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(item.variant_selections).map(([key, value]) => (
                  <Chip
                    key={key}
                    label={`${key}: ${value}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: '0.75rem',
                      borderColor: 'var(--catalog-primary-color, #1976d2)',
                      color: 'var(--catalog-primary-color, #1976d2)',
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {additionalQuantity > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', textTransform: 'capitalize' }}>
                Extra {quantityUnitLabelPlural}:
              </Typography>
              <Chip
                label={`+${additionalQuantity} ${quantityUnitLabelPlural}`}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.75rem',
                  borderColor: 'success.main',
                  color: 'success.main',
                }}
              />
            </Box>
          )}

          {Array.isArray(item.pricing_info?.recurring_bolt_ons) && item.pricing_info.recurring_bolt_ons.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Recurring bolt-ons:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {item.pricing_info.recurring_bolt_ons.map((addon) => (
                  <Chip
                    key={addon.addon_key}
                    label={`${addon.name} • $${(addon.total_price_cents / 100).toFixed(2)}/mo`}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: '0.75rem',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {Array.isArray(item.pricing_info?.one_time_addons) && item.pricing_info.one_time_addons.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                One-time add-ons:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {item.pricing_info.one_time_addons.map((addon) => (
                  <Chip
                    key={addon.addon_key}
                    label={`${addon.name}${addon.quantity > 1 ? ` ×${addon.quantity}` : ''} • $${(addon.total_price_cents / 100).toFixed(2)} one-time`}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: '0.75rem',
                      borderColor: 'warning.main',
                      color: 'warning.main',
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {multiLocationSites.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Multi-location quote:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {multiLocationSites.map((site: any) => (
                  <Box key={site.site_key || site.site_name} sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {site.site_name}: {site.option_label || site.provider}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {site.address}
                    </Typography>
                    <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 700 }}>
                      ${((Number(site.mrc_cents) || 0) / 100).toLocaleString('en-US')}/mo · ${((Number(site.nrc_cents) || 0) / 100).toLocaleString('en-US')} NRC
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Selected Perk - Always Display Winner Perk */}
          {item.selected_perk_name && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: 'success.50',
                borderRadius: 1,
                border: 1,
                borderColor: 'success.200',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 'bold', color: 'success.main' }}
              >
                ✓ Selected Perk Package
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {item.selected_perk_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Perk provided by partner • Includes all benefits
              </Typography>
            </Box>
          )}
        </Box>

        {/* Pricing */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 'bold', color: 'var(--catalog-primary-color, #1976d2)' }}
            >
              {getPriceDisplay(item)}/month
            </Typography>
            {typeof item.pricing_info?.additional_user_total_cents === 'number' &&
              item.pricing_info.additional_user_total_cents > 0 && (
                <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                  Includes ${(item.pricing_info.additional_user_total_cents / 100).toFixed(2)}/month
                  for {item.pricing_info.additional_users || item.service_configuration?.additional_users || 0}{' '}
                  extra {(item.pricing_info.additional_users || item.service_configuration?.additional_users || 0) === 1
                    ? item.pricing_info.quantity_unit_label || 'user'
                    : `${item.pricing_info.quantity_unit_label || 'user'}s`}
                </Typography>
              )}
            {typeof item.pricing_info?.recurring_bolt_ons_total_cents === 'number' && item.pricing_info.recurring_bolt_ons_total_cents > 0 && (
              <Typography variant="caption" color="primary.main" sx={{ display: 'block' }}>
                Includes ${(item.pricing_info.recurring_bolt_ons_total_cents / 100).toFixed(2)}/month in recurring bolt-ons
              </Typography>
            )}
            {typeof item.pricing_info?.one_time_total_cents === 'number' && item.pricing_info.one_time_total_cents > 0 && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                + ${(item.pricing_info.one_time_total_cents / 100).toFixed(2)} one-time due today
              </Typography>
            )}
            {item.term_months && (
              <Typography variant="caption" color="text.secondary">
                {item.term_months} month term
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ViewIcon />}
              onClick={(e) => {
                e.stopPropagation();
                if (item.variant_key || (item.service_configuration && Object.keys(item.service_configuration).length > 0)) {
                  onEditVariants(item.service_id, item);
                } else {
                  onViewDetails(item.service_id, item);
                }
              }}
              sx={{
                fontSize: '0.75rem',
                borderColor: 'var(--catalog-primary-color, #1976d2)',
                color: 'var(--catalog-primary-color, #1976d2)',
                '&:hover': {
                  borderColor: 'var(--catalog-primary-color, #1976d2)',
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
            >
              {item.variant_key || (item.service_configuration && Object.keys(item.service_configuration).length > 0)
                ? 'Edit Configuration'
                : 'View Details'}
            </Button>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          {item.status === 'saved_for_later' ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ShoppingCartIcon />}
              onClick={() => onMoveToCart(item.id)}
              sx={{
                fontSize: '0.75rem',
                borderColor: 'var(--catalog-primary-color, #1976d2)',
                color: 'var(--catalog-primary-color, #1976d2)',
                '&:hover': {
                  borderColor: 'var(--catalog-primary-color, #1976d2)',
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
            >
              Move to Cart
            </Button>
          ) : (
            <Button
              size="small"
              variant="text"
              startIcon={<SaveForLaterIcon />}
              onClick={() => onSaveForLater(item.id)}
              sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
            >
              Save for Later
            </Button>
          )}
          <Button
            size="small"
            variant="text"
            startIcon={<DeleteIcon />}
            onClick={() => onRemove(item.id)}
            sx={{ fontSize: '0.75rem', color: 'error.main' }}
          >
            Remove
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CartItem;
